// Editing one Block in place.
//
// The textarea takes the paragraph's place in the same serif at the same size,
// and its padding is pulled back out with a negative margin, so the words do
// not move when the Author clicks them. A Block that jumped a few pixels on
// every click would cost more attention over a Book than the edit itself.

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TextArea } from "../../../components/control/TextArea";
import { isCommitChord } from "./shortcuts";

/** Cancels the pull-back so the control's own frame sits in the row's gap. */
const ALIGN_TO_TEXT = "-mx-[11px] -my-[9px]";

export function BlockEditor({
  initialText,
  busy,
  onCommit,
  onDraftChange,
  onCancel,
}: {
  onDraftChange?: ((text: string) => void) | undefined;
  initialText: string;
  busy: boolean;
  /** Called with the trimmed text; an unchanged or empty text cancels instead. */
  onCommit: (text: string, viaKeyboard: boolean) => void;
  onCancel: (viaKeyboard: boolean) => void;
}) {
  const { t } = useTranslation("review");
  const [text, setText] = useState(initialText);
  const originalText = useRef(initialText);
  // Escape has to beat the commit that blur would otherwise run. The latch is
  // a ref, not state: taking a focused element out of the document can fire a
  // native blur, and a state update scheduled a moment earlier would not be
  // visible to the handler that then reads it.
  const cancelled = useRef(false);
  // Whether the Author left the editor with a key or with a click. The screen
  // puts the focus back on the row for the first and leaves it where the click
  // landed for the second, so typing into the Instruction field right after an
  // edit types into the field rather than firing the screen's shortcuts.
  const viaKeyboard = useRef(false);
  // One commit per piece of typing. The editor now stays open until the server
  // has taken the edit, so the commit chord's own blur and a blur that follows
  // it would both reach this handler and send the same PATCH twice — and the
  // second one, carrying the version id from before the first forked, would
  // fork a second time.
  const committed = useRef(false);
  const frame = useRef<HTMLDivElement>(null);

  // A commit the server refused leaves this editor open with the Author's
  // words still in it, and `busy` goes back to false. Without this the focus
  // would sit on the body where the blur left it: every letter they typed
  // would be one of the screen's shortcuts, the editor would take none of
  // them, and only Escape would get out — which throws the sentence away.
  // Arming the commit again is the other half: an unchanged blur has to be
  // able to send the same edit a second time.
  const wasBusy = useRef(false);
  useEffect(() => {
    if (busy) {
      wasBusy.current = true;
      return;
    }
    if (!wasBusy.current) return;
    wasBusy.current = false;
    committed.current = false;
    frame.current?.querySelector("textarea")?.focus();
  }, [busy]);

  const commit = () => {
    // The textarea is disabled while the edit is in flight, and disabling a
    // focused element fires a native blur; without this the same edit would go
    // out twice.
    if (busy || committed.current) return;
    committed.current = true;
    const next = text.trim();
    const keyboard = viaKeyboard.current;
    if (cancelled.current || next === "" || next === initialText) onCancel(keyboard);
    else onCommit(next, keyboard);
  };

  return (
    <div ref={frame} className={ALIGN_TO_TEXT} data-typing="block">
      <TextArea
        autosize
        serif
        autoFocus
        rows={1}
        value={text}
        disabled={busy}
        aria-label={t("editor.label")}
        onChange={(event) => {
          // Typing again after a refusal arms the commit once more, so the
          // Author can correct the text and try the same edit a second time.
          committed.current = false;
          setText(event.target.value);
          onDraftChange?.(event.target.value);
        }}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            cancelled.current = true;
            viaKeyboard.current = true;
            onDraftChange?.(originalText.current);
            onCancel(true);
            return;
          }
          if (isCommitChord(event)) {
            event.preventDefault();
            viaKeyboard.current = true;
            event.currentTarget.blur();
          }
        }}
      />
    </div>
  );
}
